import {
  Component,
  computed,
  effect,
  inject,
  linkedSignal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { disabled, form, readonly } from '@angular/forms/signals';
import { ActivatedRoute } from '@angular/router';
import { TabBase } from '@core/base/tab.base';
import { DvFormWrapperComponent, DvSectionComponent } from '@devinco/ui/dds';
import { DvSignalFormComponent } from '@devinco/ui/signal-forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Entity, NEW_ENTITY_ROUTE_PARAM } from '@shared/constants/entity';
import { SignalFormService } from '@shared/dv-signal-form/services/signal-form.service';
import {
  DamageReportDto,
  DamageReportFormModel,
} from '../../damage-reports-grid/interfaces';
import { DamageReportInformationTabService } from './damage-report-information-tab.service';
import { DamageReportsMetadataService } from '@pages/damage-reports-page/services/damage-reports-metadata.service';
import {
  maxLengthValidator,
  notInFutureValidator,
  requiredValidator,
  SHORT_DESCRIPTION_MAX_LENGTH,
} from '@shared/dv-signal-form';
import { DvSavePanelModule } from '@devinco/ui/legacy';
import { PendingChanges } from '@core/guards/pending-changes.guard';
import { map, Observable, of, take, tap } from 'rxjs';
import { toSignal } from '@angular/core/rxjs-interop';
import { RestApiService } from '@core/services/rest-api.service';
import {
  DamageReportStatus,
  DamageReportStatusId,
} from '@shared/constants/damage-report-status';
import {
  DamageReportSeverity,
  DamageReportSeverityId,
} from '@shared/constants/damage-report-sverity';
import { SplitterWithGridHelperService } from '@features/vt-grid/services/splitter-with-grid-helper.service';
import DateUtils from '@shared/utils/date.utils';
import { UserMetadataService } from '@core/services/user-metadata.service';

type VehicleDropdownRow = Partial<{
  RegistrationNumber: string;
  VehicleDescription: string;
  UserDescription: string;
  UserMobile: string;
  DefaultUserID: number;
  VehicleID: number;
}>;

@Component({
  selector: 'app-damage-report-information-tab',
  templateUrl: './damage-report-information-tab.component.html',
  imports: [
    DvFormWrapperComponent,
    DvSignalFormComponent,
    TranslateModule,
    DvSectionComponent,
    DvSavePanelModule,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [DamageReportInformationTabService, DamageReportsMetadataService],
})
export class DamageReportInformationTabComponent
  extends TabBase
  implements PendingChanges
{
  private formService = inject(DamageReportInformationTabService);
  private signalFormService = inject(SignalFormService);
  private api = inject(RestApiService);
  private translate = inject(TranslateService);
  private gridEntityService = inject(SplitterWithGridHelperService);
  private metaData = inject(UserMetadataService);
  private lastRequestedMobileDriverId?: number;

  protected damageInformationElements =
    this.formService.getDamageInformationElements();

  protected incidentDetailsElements =
    this.formService.getIncidentDetailsElements();

  protected otherVehicleElements = this.formService.getOtherVehicleElements();

  protected damageDetailsElements = this.formService.getDamageDetailsElements();

  protected additionalInformationElements =
    this.formService.additionalInformationElements();

  private entityIdSignal = toSignal(this.entityId$);
  private isNewEntity = computed(
    () => this.entityIdSignal() === NEW_ENTITY_ROUTE_PARAM
  );

  private readonly formElements = computed(() => [
    ...this.damageInformationElements,
    ...this.incidentDetailsElements,
    ...this.damageDetailsElements(),
    ...this.otherVehicleElements,
    ...this.additionalInformationElements,
  ]);

  private resources =
    this.signalFormService.getFormResources<DamageReportFormModel>({
      defaultValue: {
        DamageReportStatusID: DamageReportStatusId[DamageReportStatus.Draft],
        IncidentDateTime: `${DateUtils.convertDate(new Date())}T${DateUtils.getTimeString(new Date())}:00`,
        DamageSeverityID: DamageReportSeverityId[DamageReportSeverity.Minor],
        CreatedByDescription: `${this.metaData.getUserData().UserDescription} (${this.metaData.getUserData().Username})`,
        PoliceInvolved: false,
        PersonalInjury: false,
        WitnessInvolved: false,
      } as DamageReportFormModel,
      entityId: this.entityIdSignal,
      entity: Entity.DamageReport,
      elements: this.formElements(),
    });

  formModel = this.resources.formModel;

  private selectedVehicleRow = linkedSignal<
    { entityId: unknown; reg: unknown[]; desc: unknown[] },
    VehicleDropdownRow | undefined
  >({
    source: () => ({
      entityId: this.entityIdSignal(),
      reg: this.resources.selectedItems['RegistrationNumber'](),
      desc: this.resources.selectedItems['TrackerObjectDescription'](),
    }),
    computation: (source, previous) => {
      if (source.entityId !== previous?.source.entityId) {
        return undefined;
      }

      const regChanged = source.reg !== previous?.source.reg;
      const descChanged = source.desc !== previous?.source.desc;

      let changedItem: unknown;
      if (regChanged) {
        changedItem = source.reg[0];
      } else if (descChanged) {
        changedItem = source.desc[0];
      }

      const row = changedItem as VehicleDropdownRow | undefined;
      return row && 'UserDescription' in row ? row : previous?.value;
    },
  });

  fieldTree = form(this.formModel, path => {
    requiredValidator(path.IncidentDateTime);
    notInFutureValidator(path.IncidentDateTime);
    requiredValidator(path.ShortDescription);
    maxLengthValidator(path.ShortDescription, SHORT_DESCRIPTION_MAX_LENGTH);

    const statusOpen = ({ valueOf }: any): boolean =>
      valueOf(path.DamageReportStatusID) !== 1;

    readonly(path.CreatedByDescription);
    readonly(path.IncidentDateTime, statusOpen);
    readonly(path.RegistrationNumber, statusOpen);
    readonly(path.TrackerObjectDescription, statusOpen);
    readonly(path.DriverID, statusOpen);
    readonly(path.Mobile);
    readonly(path.Location, statusOpen);
    readonly(path.ShortDescription, statusOpen);
    readonly(path.WeatherCondition, statusOpen);
    readonly(path.IncidentTypeID, statusOpen);
    readonly(path.IncidentDescription, statusOpen);
    readonly(path.DamageLocation, statusOpen);
    readonly(path.DamageDescription, statusOpen);
    disabled(path.DamageSeverityID, statusOpen);
    disabled(path.OtherVehicleInvolved, statusOpen);
    readonly(path.OtherVehicleRegistrationNumber, statusOpen);
    readonly(path.OtherVehicleDriverName, statusOpen);
    readonly(path.OtherVehicleDriverContactInfo, statusOpen);
    readonly(path.OtherVehicleInsuranceCompany, statusOpen);
    readonly(path.OtherVehicleDriverLicenseNumber, statusOpen);
    readonly(path.OtherVehicleCountryCode, statusOpen);
    disabled(path.PoliceInvolved, statusOpen);
    readonly(path.PoliceReportNumber, statusOpen);
    disabled(path.PersonalInjury, statusOpen);
    disabled(path.WitnessInvolved, statusOpen);
    readonly(path.WitnessName, statusOpen);
    readonly(path.WitnessContactInfo, statusOpen);
    readonly(path.MyVehicleIncidentAction, statusOpen);
    readonly(path.OtherVehicleIncidentAction, statusOpen);

    readonly(
      path.OtherVehicleRegistrationNumber,
      ({ valueOf }) => !valueOf(path.OtherVehicleInvolved)
    );
    readonly(
      path.OtherVehicleDriverName,
      ({ valueOf }) => !valueOf(path.OtherVehicleInvolved)
    );
    readonly(
      path.OtherVehicleDriverContactInfo,
      ({ valueOf }) => !valueOf(path.OtherVehicleInvolved)
    );
    readonly(
      path.OtherVehicleInsuranceCompany,
      ({ valueOf }) => !valueOf(path.OtherVehicleInvolved)
    );
    readonly(
      path.OtherVehicleDriverLicenseNumber,
      ({ valueOf }) => !valueOf(path.OtherVehicleInvolved)
    );
    readonly(
      path.OtherVehicleCountryCode,
      ({ valueOf }) => !valueOf(path.OtherVehicleInvolved)
    );

    readonly(
      path.PoliceReportNumber,
      ({ valueOf }) => !valueOf(path.PoliceInvolved)
    );

    readonly(path.WitnessName, ({ valueOf }) => !valueOf(path.WitnessInvolved));

    readonly(
      path.WitnessContactInfo,
      ({ valueOf }) => !valueOf(path.WitnessInvolved)
    );
  });

  isSaveDisabled = computed(() => {
    const fieldTree = this.fieldTree();
    return !fieldTree.dirty() || fieldTree.invalid();
  });

  hasChanges = computed(() => {
    const fieldTree = this.fieldTree();
    return fieldTree.dirty() && !fieldTree.invalid();
  });

  constructor() {
    super(inject(ActivatedRoute).parent!);

    effect(() => {
      if (!this.isNewEntity()) return;
      this.fieldTree.IncidentDateTime().markAsDirty();
    });

    effect(() => {
      if (!this.isNewEntity()) return;
      this.resources.selectedItems['DamageReportStatusID'].set([
        {
          DamageReportStatusID: DamageReportStatusId.Draft,
          TranslationKey: this.translate.instant('DamageReportStatus.Draft'),
        },
      ]);
    });

    effect(() => {
      const row = this.selectedVehicleRow();
      if (!row) return;

      const current = this.formModel();
      if (
        current.RegistrationNumber === row.RegistrationNumber &&
        current.TrackerObjectDescription === row.VehicleDescription &&
        current.DriverID === (row.DefaultUserID ?? current.DriverID) &&
        current.TrackerObjectID === (row.VehicleID ?? current.TrackerObjectID)
      ) {
        return;
      }

      this.formModel.update(m => ({
        ...m,
        RegistrationNumber: row.RegistrationNumber ?? m.RegistrationNumber,
        TrackerObjectDescription:
          row.VehicleDescription ?? m.TrackerObjectDescription,
        DriverID: row.DefaultUserID ?? m.DriverID,
        DriverDescription: row.UserDescription ?? '',
        TrackerObjectID: row.VehicleID ?? m.TrackerObjectID,
      }));

      this.resources.selectedItems['RegistrationNumber'].set([row]);
      this.resources.selectedItems['TrackerObjectDescription'].set([row]);
      this.resources.selectedItems['DriverID'].set(
        row.DefaultUserID
          ? [
              {
                UserDescription: row.UserDescription,
                UserID: row.DefaultUserID,
              },
            ]
          : []
      );

      this.applyDriverMobile(row.DefaultUserID);
    });

    effect(() => {
      const model = this.formModel();
      if (model.RegistrationNumber || model.TrackerObjectDescription) return;
      if (!model.TrackerObjectID) return;

      this.formModel.update(m => ({ ...m, TrackerObjectID: null }));
    });

    effect(() => {
      const driverRow = this.resources.selectedItems['DriverID']()[0] as
        | { Mobile?: string }
        | undefined;

      if (!driverRow || driverRow.Mobile === undefined) return;

      const mobile = driverRow.Mobile;
      if (this.formModel().Mobile === mobile) return;

      this.formModel.update(m => ({ ...m, Mobile: mobile }));
    });

    let prevToggles: { police?: boolean; witness?: boolean; other?: boolean } =
      {};

    effect(() => {
      const model = this.formModel();
      const updates: Partial<DamageReportDto> = {};

      const policeTurnedOff =
        prevToggles.police === true && !model.PoliceInvolved;
      const witnessTurnedOff =
        prevToggles.witness === true && !model.WitnessInvolved;
      const otherVehicleTurnedOff =
        prevToggles.other === true && !model.OtherVehicleInvolved;

      prevToggles = {
        police: model.PoliceInvolved,
        witness: model.WitnessInvolved,
        other: model.OtherVehicleInvolved,
      };

      if (otherVehicleTurnedOff) {
        updates.OtherVehicleRegistrationNumber = '';
        updates.OtherVehicleDriverName = '';
        updates.OtherVehicleDriverContactInfo = '';
        updates.OtherVehicleInsuranceCompany = '';
        updates.OtherVehicleDriverLicenseNumber = '';
        updates.OtherVehicleCountryCode = '';
      } else {
        const otherVehicleHasData = [
          model.OtherVehicleRegistrationNumber,
          model.OtherVehicleDriverName,
          model.OtherVehicleDriverContactInfo,
          model.OtherVehicleInsuranceCompany,
          model.OtherVehicleDriverLicenseNumber,
          model.OtherVehicleCountryCode,
        ].some(v => !!v);

        if (otherVehicleHasData && !model.OtherVehicleInvolved) {
          updates.OtherVehicleInvolved = true;
        }
      }

      if (policeTurnedOff) {
        updates.PoliceReportNumber = '';
      } else if (model.PoliceReportNumber && !model.PoliceInvolved) {
        updates.PoliceInvolved = true;
      }

      if (witnessTurnedOff) {
        updates.WitnessName = '';
        updates.WitnessContactInfo = '';
      } else {
        const witnessHasData = [
          model.WitnessName,
          model.WitnessContactInfo,
        ].some(v => !!v);

        if (witnessHasData && !model.WitnessInvolved) {
          updates.WitnessInvolved = true;
        }
      }

      if (Object.keys(updates).length) {
        this.formModel.update(m => ({ ...m, ...updates }));
      }
    });
  }

  pendingChangesSave = (): Observable<boolean> => this.performSave();

  pendingChangesDiscard = (): void => {
    this.fieldTree().reset(this.formModel());
  };

  onSave(): void {
    this.performSave().pipe(take(1)).subscribe();
  }

  private applyDriverMobile(driverId?: number): void {
    if (!driverId || driverId === this.lastRequestedMobileDriverId) return;
    this.lastRequestedMobileDriverId = driverId;

    this.api
      .getInfo<{ Mobile?: string }>(Entity.User, String(driverId), ['Mobile'])
      .pipe(take(1))
      .subscribe(data => {
        const mobile = data.Mobile ?? '';
        if (this.formModel().Mobile === mobile) return;

        this.formModel.update(m => ({ ...m, Mobile: mobile }));
      });
  }

  private performSave(): Observable<boolean> {
    const dirtyValues =
      this.signalFormService.getDirtyValues<DamageReportFormModel>(
        this.fieldTree,
        this.formElements(),
        { reduceDropdownsToId: 'single' }
      );

    if (Object.keys(dirtyValues).length) {
      dirtyValues.Mobile = this.formModel().Mobile;
      dirtyValues.TrackerObjectID = this.formModel().TrackerObjectID;
      dirtyValues.IncidentDateTime = this.formModel().IncidentDateTime;
    }

    if (!Object.keys(dirtyValues).length) {
      this.fieldTree().reset(this.formModel());
      return of(true);
    }

    const entityId = this.entityIdSignal();

    const idToSave = entityId === NEW_ENTITY_ROUTE_PARAM ? '0' : entityId;

    if (!idToSave) return of(false);

    const statusChangedToDraft =
      dirtyValues.DamageReportStatusID ===
      DamageReportStatusId[DamageReportStatus.Draft];

    return this.api.upsert(Entity.DamageReport, idToSave, dirtyValues).pipe(
      tap(id => {
        if (entityId === NEW_ENTITY_ROUTE_PARAM) {
          this.gridEntityService.addNewlyCreatedItemToGrid(
            Entity.DamageReport,
            Number(id)
          );
        } else if (statusChangedToDraft) {
          this.gridEntityService.refreshGrid();
        } else {
          this.gridEntityService.refreshRowData(idToSave!);
        }

        this.fieldTree().reset(this.formModel());
      }),
      map(() => true)
    );
  }
}
